import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePlanSelection } from '../../../portals/customer/contexts/PlanSelectionContext';
import './PremiumUpdateModal.css';

interface PremiumUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  adjustedPrice?: number | null;
  pabaLoading?: boolean;
}

const PremiumUpdateModal: React.FC<PremiumUpdateModalProps> = ({
  isOpen,
  onClose,
  adjustedPrice,
  pabaLoading = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const okayButtonRef = useRef<HTMLButtonElement>(null);
  const { selectedPlan, getTempDataValue } = usePlanSelection();

  useEffect(() => {
    if (isOpen) {
      // Focus the okay button when modal opens
      okayButtonRef.current?.focus();
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Calculate updated premium using the API response or fallback to temp data
  const getUpdatedPremium = () => {
    // ENHANCED: Check for forex conversion data first
    const isForexConverted = getTempDataValue('isForexConverted') as boolean;
    const convertedCurrencyCode = getTempDataValue('convertedCurrencyCode') as string;
    const convertedRateAmount = getTempDataValue('convertedRateAmount') as number;
    
    // Get standard tempData values from context
    const tempDataCurrencyCode = getTempDataValue('currencyCode') as string;
    const tempDataRateAmount = getTempDataValue('rateAmount') as number;
    
    // Determine currency code and amount with enhanced priority logic
    let currencyCode = 'PHP'; // Final fallback
    let currentAmount = 0;

    // Priority 1: Use converted currency data if forex conversion occurred
    if (isForexConverted && convertedCurrencyCode && convertedCurrencyCode.trim() !== '' &&
        convertedRateAmount && convertedRateAmount > 0) {
      currencyCode = convertedCurrencyCode;
      currentAmount = convertedRateAmount;
      console.log('PremiumUpdateModal: Using converted currency data - currencyCode:', currencyCode, 'rateAmount:', currentAmount);
    }
    // Priority 2: Use current tempData (which should be updated with converted values)
    else if (tempDataCurrencyCode && tempDataCurrencyCode.trim() !== '' &&
             tempDataRateAmount && tempDataRateAmount > 0) {
      currencyCode = tempDataCurrencyCode;
      currentAmount = tempDataRateAmount;
      console.log('PremiumUpdateModal: Using tempData from context - currencyCode:', currencyCode, 'rateAmount:', currentAmount);
    }
    // Priority 3: Use selectedPlan data (original plan data)
    else if (selectedPlan?.rate?.currency?.currencyCode &&
             selectedPlan.rate.currency.currencyCode.trim() !== '' &&
             selectedPlan?.rate?.rateAmount &&
             selectedPlan.rate.rateAmount > 0) {
      currencyCode = selectedPlan.rate.currency.currencyCode;
      currentAmount = selectedPlan.rate.rateAmount;
      console.log('PremiumUpdateModal: Using selectedPlan data - currencyCode:', currencyCode, 'rateAmount:', currentAmount);
    }
    
    // Use API response if available, otherwise fallback to static calculation
    if (adjustedPrice !== null && adjustedPrice !== undefined) {
      return { currencyCode, amount: adjustedPrice };
    }
    
    // Static fallback calculation (double for demo) - only if we have a current amount
    if (currentAmount > 0) {
      const updatedAmount = Math.round(currentAmount * 2);
      return { currencyCode, amount: updatedAmount };
    }

    // Final fallback - should rarely be reached
    return { currencyCode: 'PHP', amount: 0 };
  };

  // Calculate current premium with enhanced forex conversion support
  const getCurrentPremium = () => {
    // ENHANCED: Check for forex conversion data first
    const isForexConverted = getTempDataValue('isForexConverted') as boolean;
    const convertedCurrencyCode = getTempDataValue('convertedCurrencyCode') as string;
    const convertedRateAmount = getTempDataValue('convertedRateAmount') as number;
    
    // Get standard tempData values from context
    const tempDataCurrencyCode = getTempDataValue('currencyCode') as string;
    const tempDataRateAmount = getTempDataValue('rateAmount') as number;
    
    // Priority 1: Use converted currency data if forex conversion occurred
    if (isForexConverted && convertedCurrencyCode && convertedCurrencyCode.trim() !== '' &&
        convertedRateAmount && convertedRateAmount > 0) {
      console.log('PremiumUpdateModal: Using converted currency for current premium - currencyCode:', convertedCurrencyCode, 'rateAmount:', convertedRateAmount);
      return {
        currencyCode: convertedCurrencyCode,
        amount: convertedRateAmount
      };
    }
    // Priority 2: Use current tempData (which should be updated with converted values)
    else if (tempDataCurrencyCode && tempDataCurrencyCode.trim() !== '' &&
             tempDataRateAmount && tempDataRateAmount > 0) {
      console.log('PremiumUpdateModal: Using tempData from context for current premium - currencyCode:', tempDataCurrencyCode, 'rateAmount:', tempDataRateAmount);
      return {
        currencyCode: tempDataCurrencyCode,
        amount: tempDataRateAmount
      };
    }
    // Priority 3: Use selectedPlan data (original plan data)
    else if (selectedPlan?.rate?.currency?.currencyCode &&
             selectedPlan.rate.currency.currencyCode.trim() !== '' &&
             selectedPlan?.rate?.rateAmount &&
             selectedPlan.rate.rateAmount > 0) {
      console.log('PremiumUpdateModal: Using selectedPlan for current premium - currencyCode:', selectedPlan.rate.currency.currencyCode, 'rateAmount:', selectedPlan.rate.rateAmount);
      return {
        currencyCode: selectedPlan.rate.currency.currencyCode,
        amount: selectedPlan.rate.rateAmount
      };
    }
    // Final fallback - should rarely be reached
    return { currencyCode: 'PHP', amount: 0 };
  };

  const currentPremium = getCurrentPremium();

  const updatedPremium = getUpdatedPremium();

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="premium-update-modal-backdrop"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="premium-modal-title"
      aria-describedby="premium-modal-description"
    >
      <div className="premium-update-modal" ref={modalRef}>
        <div className="premium-update-modal-header">
          <h2 id="premium-modal-title" className="premium-update-modal-title">
            Premium Update Based on Your Age           
          </h2>
          <button
            className="premium-update-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>        
        </div>
        
        <div className="premium-update-modal-content">
          <p id="premium-modal-description" className="premium-update-modal-message">
            We noticed that you're over 75 years old. Because of this, your travel insurance premium has been adjusted.
          </p>                     
          <div className="premium-comparison-container">
            {/* Current Premium Card */}
            <div className="premium-card current-premium">
              <div className="premium-icon">
                <div className="premium-icon-circle">
                  <span className="premium-icon-text">P</span>
                </div>
              </div>
              <div className="premium-amount">
                {currentPremium.currencyCode} {currentPremium.amount}
              </div>
              <div className="premium-label">Current Premium</div>
            </div>

            {/* Arrow */}
            <div className="premium-arrow-container">
              <div className="premium-arrow">
                <svg width="40" height="16" viewBox="0 0 40 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 8H39M39 8L32 1M39 8L32 15" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 4"/>
                </svg>
              </div>
              <div className="premium-arrow-label">After calculation</div>
            </div>

            {/* Updated Premium Card */}
            <div className="premium-card updated-premium">
              <div className="premium-icon">
                <div className="premium-icon-circle">
                  {pabaLoading ? (
                    <div className="loading-spinner" />
                  ) : (
                    <span className="premium-icon-text">P</span>
                  )}
                </div>
              </div>
              <div className="premium-amount">
                {pabaLoading ? (
                  'Calculating...'
                ) : (
                  `${updatedPremium.currencyCode} ${updatedPremium.amount}`
                )}
              </div>
              <div className="premium-label">Updated Premium</div>
            </div>
          </div>

          <p className="premium-update-modal-footer-text">
            Here's the current premium from the plan you selected earlier and the updated premium after the adjustment.
          </p>
        </div>

        <div className="premium-update-modal-actions">
          <button
            ref={okayButtonRef}
            className="premium-update-modal-okay-btn"
            onClick={onClose}
            type="button"
            disabled={pabaLoading}
          >
            {pabaLoading ? 'Calculating...' : 'Okay'}
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal using React portal to ensure it's rendered at document body level
  return createPortal(modalContent, document.body);
};

export default PremiumUpdateModal;